import React, { useCallback, useMemo, useRef } from 'react'
import { VariableSizeList as List, ListChildComponentProps, areEqual } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import { Box, Flex } from 'rebass'
import { useResizeDetector } from 'react-resize-detector'
import { useDispatch, useSelector } from 'react-redux'

import { SubmissionState, SUBMISSION_VIEW_ENUM, SUBMISSION_TASK_STATUS_ENUM } from 'state/submissions'
import {
    clearSelectedMediaContent,
    makeShowCommentAndVoteSelector,
    selectReviewPendingSubmission,
    selectSelectedSubmissionView,
    selectSelectedVerifyTab,
    selectSidebarStep,
    selectSidebarTask,
    setSelectedVerifyTab,
    SubmissionProgressProps,
    VERIFY_TAB_TYPE,
    selectShowTaskStepInfoInVerify,
    selectCheckCanReviewSubmissionByStep,
} from 'state/review'
import { VerifyProgressBlock } from '../ProgressBlock'
import { useNewTranslation } from 'hooks/index'
import DiscussSubmission from './DiscussSubmission'
import ReviewSubmission from './ReviewSubmission'
import { StepState, TaskState } from 'state/tasks'
import TabSwitcherGray, { Tab } from 'components/TabSwitcherGray'
import { State } from 'state'
import EditRequestHistoryButton from '../EditRequestHistory'
import Divider from 'components/Divider'
import { spacing } from 'particles/styles'
import CommentAndVoteSummary from '../ProgressBlock/CommentAndVoteSummary'
import Text from 'particles/Text'
import { coreColors } from 'particles/colors'
import styled from 'styled-components'
import PartiposterProfile from '../../PartiposterProfile'
import { useTheme } from 'particles/themes/styled'
import { HandleEditTagChange, HandleRejectTagChange } from 'components/utils/review'
import ResubmittedIndicator from '../ResubmittedIndicator'

interface VerifyTabsProps {
    submissionUuid?: string
    submissionProgress: SubmissionProgressProps
    step: StepState
    task: TaskState
    handleEditTagChange: HandleEditTagChange
    handleRejectTagChange: HandleRejectTagChange
    isInsideBatch?: boolean
}

export const VerifyTabs: React.FC<VerifyTabsProps> = ({
    submissionUuid,
    submissionProgress,
    step,
    task,
    handleEditTagChange,
    handleRejectTagChange,
    isInsideBatch = true,
}) => {
    const dispatch = useDispatch()
    const [newT] = useNewTranslation()
    const selectedTabEditor = useSelector(selectSelectedVerifyTab(submissionProgress.uuid))
    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)
    const selectShowCommentAndVote = useMemo(makeShowCommentAndVoteSelector, [])
    const showCommentAndVote = useSelector((state: State) => selectShowCommentAndVote(state, { step }))
    const checkCanReviewSubmissionByStep = useSelector(selectCheckCanReviewSubmissionByStep)
    const canReviewSubmission = useMemo(
        () => checkCanReviewSubmissionByStep(step.type),
        [checkCanReviewSubmissionByStep, step],
    )

    const setSelectedTabEditor = useCallback(
        (type: VERIFY_TAB_TYPE) => {
            if (selectedTabEditor !== type) {
                dispatch(setSelectedVerifyTab({ submissionUuid: submissionProgress.uuid, type }))
                dispatch(clearSelectedMediaContent({ submissionUuid: submissionProgress.uuid }))
            }
        },
        [submissionProgress, selectedTabEditor],
    )

    const tabs: Tab[] = useMemo(() => {
        const result = [] as Tab[]

        if (showCommentAndVote) {
            result.push({
                id: VERIFY_TAB_TYPE.DISCUSSION,
                label: newT('submission.review.discussionTabLabel', 'Discussion'),
                render: function Discussion() {
                    return <DiscussSubmission step={step} submissionProgress={submissionProgress} />
                },
            })
        }

        if (
            (selectedSubmissionView === SUBMISSION_VIEW_ENUM.PENDING ||
                selectedSubmissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS) &&
            canReviewSubmission
        ) {
            result.unshift({
                id: VERIFY_TAB_TYPE.REVIEW,
                label: newT('submission.review.reviewTabLabel', 'Review'),
                render: function Review() {
                    return (
                        <ReviewSubmission
                            submissionUuid={submissionUuid}
                            step={step}
                            task={task}
                            submissionProgress={submissionProgress}
                            handleEditTagChange={handleEditTagChange}
                            handleRejectTagChange={handleRejectTagChange}
                            isInsideBatch={isInsideBatch}
                        />
                    )
                },
            })
        }

        return result
    }, [selectedSubmissionView, submissionProgress, step, submissionUuid])
    return (
        <TabSwitcherGray
            selectedTab={selectedTabEditor}
            tabs={tabs}
            setTabEditorState={(tabId: VERIFY_TAB_TYPE): void => {
                setSelectedTabEditor(tabId)
            }}
        />
    )
}

const Cell: React.FC<ListChildComponentProps> = React.memo(({ index, style, data }) => {
    const [newT] = useNewTranslation()
    const { submissions, setRowHeight, handleEditTagChange, handleRejectTagChange } = data
    const submission = submissions[index]
    const sidebarStep = useSelector(selectSidebarStep)
    const sidebarTask = useSelector(selectSidebarTask)
    const showTaskStepInfo = useSelector(selectShowTaskStepInfoInVerify)
    const rowRef = useRef<any>({})
    const theme = useTheme()
    const onResize = useCallback((width, height) => {
        setRowHeight(index, height)
    }, [])
    useResizeDetector({
        onResize,
        targetRef: rowRef,
        handleWidth: false,
        refreshMode: 'debounce',
        refreshRate: 800,
        observerOptions: { box: 'border-box' },
    })

    if (!sidebarStep || !sidebarTask) {
        return null
    }

    const currentStepIndex =
        sidebarTask && sidebarStep
            ? sidebarTask.steps.findIndex((step: StepState) => sidebarStep.uuid && step.uuid == sidebarStep.uuid)
            : 0

    // Don't show edit request history if submission status is pending but task progress status is not resubmitted
    const hideEditRequestHistory =
        submission?.submission_task?.status === SUBMISSION_TASK_STATUS_ENUM.PENDING && !submission?.resubmitted

    return (
        <Box style={style} key={submission.uuid}>
            {index !== 0 && <Divider key={`divider-${submission.uuid}`} my={spacing[300]} mx={spacing[300]} />}

            <Flex ref={rowRef} sx={{ gap: spacing[300] }}>
                <Flex
                    flex={1}
                    bg={coreColors.white50}
                    p={spacing[300]}
                    flexDirection="column"
                    sx={{
                        gap: spacing[400],
                    }}
                >
                    <PartiposterProfile
                        isVerifyMode
                        submission={submission}
                        submissionUuid={submission.submission_uuid}
                        options={{
                            instagram: true,
                            facebook: sidebarTask.type === 'facebook_post',
                        }}
                    />
                    {showTaskStepInfo && (
                        <>
                            <Box>
                                <Text scale="bodyS500" textAlign="left">
                                    {sidebarTask.title}
                                </Text>

                                <Box
                                    width="95%"
                                    sx={{
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <Text scale="caption400" textAlign="left">
                                        {newT('taskType.stepNumberTitle', {
                                            itemCount: currentStepIndex + 1,
                                            firstItemCount: sidebarTask.steps ? sidebarTask.steps.length : 0,
                                            lastItemCount: sidebarStep.data.title
                                                ? sidebarStep.data.title
                                                : 'Step Title',
                                        })}
                                    </Text>
                                </Box>
                            </Box>
                            <Divider my={0} sx={{ borderTopWidth: '2px', borderColor: coreColors.grey700 }} />
                            {!hideEditRequestHistory && (
                                <ViewEditRequestCommentsBlock>
                                    {submission.resubmitted && <ResubmittedIndicator />}
                                    <EditRequestHistoryButton
                                        submissionProgressUuid={submission.uuid}
                                        partiposterId={submission.partiposterId}
                                        fontSize="s"
                                    />
                                </ViewEditRequestCommentsBlock>
                            )}
                        </>
                    )}
                    <VerifyProgressBlock task={sidebarTask} submissionProgress={submission} step={sidebarStep} />

                    <Divider my={0} />

                    <Box width="368px">
                        <CommentAndVoteSummary step={sidebarStep} submissionProgress={submission} />
                    </Box>
                </Flex>
                <Box bg={theme.colors.surface.primary} width="450px" px={spacing[300]} py={spacing[500]}>
                    <VerifyTabs
                        submissionProgress={submission}
                        step={sidebarStep}
                        task={sidebarTask}
                        handleEditTagChange={handleEditTagChange}
                        handleRejectTagChange={handleRejectTagChange}
                    />
                </Box>
            </Flex>
        </Box>
    )
}, areEqual)
Cell.displayName = 'Cell'

interface VerifyProps {
    handleEditTagChange: HandleEditTagChange
    handleRejectTagChange: HandleRejectTagChange
    submissions: SubmissionState[]
}

const Verify: React.FC<VerifyProps> = ({ submissions, handleEditTagChange, handleRejectTagChange }) => {
    const reviewPendingSubmission = useSelector(selectReviewPendingSubmission)

    const sortedSubmissions = useMemo(() => {
        if (reviewPendingSubmission) {
            const firstSubmissionIndex = submissions.findIndex(s => s.uuid === reviewPendingSubmission)
            const result = [...submissions]
            if (firstSubmissionIndex > -1) {
                result.splice(0, 0, result.splice(firstSubmissionIndex, 1)[0])
                return result
            }
        }
        return submissions
    }, [reviewPendingSubmission, submissions])

    const rowHeights = useRef<any>({})
    const listRef = useRef<any>({})

    const getRowHeight = (index: number): number => {
        return rowHeights.current[index] || 800
    }

    const setRowHeight = (index: number, size: number): void => {
        listRef.current.resetAfterIndex(0)
        rowHeights.current = { ...rowHeights.current, [index]: size }
    }
    return (
        <FlexBox>
            <AutoSizer>
                {({ height, width }: { height: number; width: number }) => (
                    <List
                        ref={listRef}
                        height={height}
                        width={width}
                        itemCount={sortedSubmissions.length}
                        itemData={{
                            submissions: sortedSubmissions,
                            setRowHeight,
                            handleEditTagChange,
                            handleRejectTagChange,
                        }}
                        itemSize={index => getRowHeight(index) + 15}
                    >
                        {Cell}
                    </List>
                )}
            </AutoSizer>
        </FlexBox>
    )
}

export default Verify

const FlexBox = styled.div`
    box-sizing: border-box;
    display: flex;
    flex: 1 1 auto;
    padding-top: ${spacing[300]};
    overflow-y: hidden;

    @media (max-width: 1280px) {
        min-width: 1111px;
    }
`

const ViewEditRequestCommentsBlock = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing[300]};
    align-items: baseline;
`
